import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FilterMetadata, MessageService } from 'primeng/api';
import { Table, TableFilterEvent } from 'primeng/table';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { FieldDivisionGroupsService } from 'src/app/services/fd-groups.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';

@Component({
  selector: 'ness-manage-fd-groups',
  templateUrl: './manage-fd-groups.component.html'
})
export class ManageFDGroupsComponent implements OnInit, OnDestroy {
  @ViewChild('fdgroupsdt') fdGroupsTable: Table;
  @ViewChild('filterInput') globalFilterEl: ElementRef;
  fieldDivisionGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  clonedFieldDivisionGroups: { [s: string]: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups } = {};
  private _fieldDivisions: ServerModels.ENESS_REF_FieldDivisions[] = [];
  private fieldDivisionsSubj: BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]> = new BehaviorSubject(this._fieldDivisions);
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.fieldDivisionsSubj.asObservable();
  cols: any[] = [
    { header: 'Field Division', field: 'ENESS_REF_FieldDivisions.FieldDivisionName' },
    { header: 'Field Division Group', field: 'GroupName' },
    { header: 'Status', field: 'Inactive' }
  ];
  rows: number = AppConstants.defaultTableRows;
  loading: boolean = false;
  AppUtils = AppUtils;

  private _destroyed$: Subject<void> = new Subject<void>();

  public get canAddEditFDGroup(): boolean {
    return Object.keys(this.fdGroupsTable?.editingRowKeys ?? {}).length > 0;
  }


  constructor(private fdGroupsService: FieldDivisionGroupsService,
    private messageService: MessageService,
    private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this._getFieldDivisionGroups();
    this._getFieldDivisionsList();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.messageService.clear();
  }

  private _getFieldDivisionGroups(): void {
    this.fdGroupsService.getFieldDivisionGroups().pipe(takeUntil(this._destroyed$)).subscribe(
      (data: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[]) => {
        this.loading = false;
        if (data && !!data.length)
          this.fieldDivisionGroups = [...data];
      }
    );
  }

  private _getFieldDivisionsList(): void {
    this.fdGroupsService.getAllFieldDivisions().pipe(takeUntil(this._destroyed$)).subscribe(
      (fds: ServerModels.ENESS_REF_FieldDivisions[]) => {
        this._fieldDivisions = fds;
        this.fieldDivisionsSubj.next(this._fieldDivisions);
      }
    );
  }

  private _cloneFieldDivisionGroup(fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups): void {
    let clonedFDGroup = {
      IntelligenceReportATFReferralGroupsID: fdGroup.IntelligenceReportATFReferralGroupsID,
      FieldDivisionID: fdGroup.FieldDivisionID,
      ENESS_REF_FieldDivisions: fdGroup.ENESS_REF_FieldDivisions,
      GroupName: fdGroup.GroupName,
      Inactive: fdGroup.Inactive,
      CreatedOn: fdGroup.CreatedOn,
      CreatedBy: fdGroup.CreatedBy,
      ModifiedOn: fdGroup.ModifiedOn,
      ModifiedBy: fdGroup.ModifiedBy
    } as ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups;

    this.clonedFieldDivisionGroups[fdGroup.IntelligenceReportATFReferralGroupsID] = Object.assign({}, clonedFDGroup);
  }

  private _saveFieldDivisionGroup(fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups, index: number): void {
    this.messageService.clear();

    let service$: Observable<ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups> = !fdGroup.IntelligenceReportATFReferralGroupsID ?
      this.fdGroupsService.addFieldDivisionGroup(fdGroup) :
      this.fdGroupsService.updateFieldDivisionGroup(fdGroup);

    service$.pipe(takeUntil(this._destroyed$)).subscribe((fdGroupData: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups) => {
      let fdGroupIdx: number = this.fieldDivisionGroups.indexOf(fdGroup);
      this.fieldDivisionGroups[fdGroupIdx] = { ...fdGroupData };

      if (this.fdGroupsTable.filteredValue) {
        this.fdGroupsTable.filteredValue[index] = { ...fdGroupData };
      }
      delete this.clonedFieldDivisionGroups[fdGroupData.IntelligenceReportATFReferralGroupsID];
    },
      (err:any) => {
        this.onRowEditCancel(fdGroup, index);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error || err.error.ExceptionMessage || err.error.Message || err.message });
      }
    );
  }

  private _createEmptyFieldDivisionGroupModel(): ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups {
    return <ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups>{
      IntelligenceReportATFReferralGroupsID: '',
      FieldDivisionID: '',
      ENESS_REF_FieldDivisions: null,
      GroupName: null,
      Inactive: false,
      CreatedOn: null,
    };
  }

  private _deleteRow(rowIndex: number) {
    this.fieldDivisionGroups = this.fieldDivisionGroups.filter((val, i) => i != rowIndex);
  }

  public onRowEditInit(fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups): void {
    if (!!fdGroup.IntelligenceReportATFReferralGroupsID) {
      this._cloneFieldDivisionGroup(fdGroup);
    }
  }

  public onRowEditSave(fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups, index: number): void {
    if (!fdGroup) {
      return;
    }
    if (!fdGroup.IntelligenceReportATFReferralGroupsID) {
      fdGroup.IntelligenceReportATFReferralGroupsID = "00000000-0000-0000-0000-000000000000";
    }
    fdGroup.ENESS_REF_FieldDivisions = this._fieldDivisions.find(f => f.FieldDivisionID.toLowerCase() === fdGroup.FieldDivisionID.toLowerCase());
    this._saveFieldDivisionGroup(fdGroup, index);
  }

  public onRowEditCancel(fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups, index: number): void {
    if (!fdGroup.IntelligenceReportATFReferralGroupsID) {
      this._deleteRow(index);
    } else {
      let fdGroupIdx: number = this.fieldDivisionGroups.indexOf(fdGroup);
      this.fieldDivisionGroups[fdGroupIdx] = this.clonedFieldDivisionGroups[fdGroup.IntelligenceReportATFReferralGroupsID];

      if (this.fdGroupsTable.filteredValue) {
        this.fdGroupsTable.filteredValue[index] = this.clonedFieldDivisionGroups[fdGroup.IntelligenceReportATFReferralGroupsID];
      }
    }
    delete this.clonedFieldDivisionGroups[fdGroup.IntelligenceReportATFReferralGroupsID];
  }

  public addFieldDivisionGroupRow(): void {
    if (!!this.fdGroupsTable) {
      this.fdGroupsTable.editingRowKeys = {};
      this.clear();
    }

    let emptyFDGroupVM = this._createEmptyFieldDivisionGroupModel();
    this.clonedFieldDivisionGroups[emptyFDGroupVM.IntelligenceReportATFReferralGroupsID] = { ...emptyFDGroupVM };
    this.fieldDivisionGroups = [...[emptyFDGroupVM], ...this.fieldDivisionGroups];
    this.fdGroupsTable.initRowEdit(emptyFDGroupVM);
    this.changeDetectorRef.markForCheck();
  }

  public clear() {
    this.fdGroupsTable.filterGlobal(null, 'contains');
    this.fdGroupsTable.reset();
  }

  public onFilterFDGroups(filterEvt: TableFilterEvent): void {
    if (!filterEvt.filters) {
      this.globalFilterEl.nativeElement.value = null;
    }
  }
}
